import { generateMnemonic, mnemonicToSeed, validateMnemonic } from '@scure/bip39'
import { wordlist as ENGLISH_WORDLIST } from '@scure/bip39/wordlists/english.js'

/**
 * Recovery Key (BIP39 24 単語) — T-C5b の暗号基盤層。
 *
 * 設計判断 (KEY_HIERARCHY.md §6 / OoKey 計画書 §4.1.4 復旧経路に準拠予定):
 *
 *   1. **BIP39 規格準拠**: 256-bit entropy → 24 word mnemonic + checksum。
 *      `@scure/bip39` (Paul Miller、audit 済) を採用。
 *      静的 import (~15 KB gzip) で十分軽量、lazy chunk 不要 (T-C2 の zxcvbn ~900 KB と桁違い)。
 *
 *   2. **wordlist は English のみ**: BIP39 仕様の一意性を担保するため
 *      English (2048 単語) のみを採用。多言語切替は意味が無い (entropy 復元は wordlist
 *      に依存するため、登録時の wordlist と検証時の wordlist が一致する必要がある)。
 *      画面表示の i18n とは別軸の話。
 *
 *   3. **mnemonicToSeed は async**: `@scure/bip39` の `mnemonicToSeed` は内部で
 *      PBKDF2-HMAC-SHA512 (2048 反復) を回す。Web Crypto は同期的にこれを提供しないため、
 *      `mnemonicToSeed` (async 版) を選び Worker は不要 (PBKDF2 2048 反復 < Argon2 < 100ms)。
 *      Argon2 は Worker 必須だが BIP39 PBKDF2 はメインスレッドで OK。
 *
 *   4. **平文 MP / Recovery Key を関数を出さない**: CLAUDE.md §2 ルール 1 と整合。
 *      `mnemonic` は引数として受け取り、内部処理後は GC 可能。caller も保持しないこと。
 *      返り値の `seed` (Uint8Array 64 byte) は呼出側で zeroize 責任を持つ
 *      (KDF 経路の入口素材 — そのまま外に出さず HKDF 等で派生してから zeroize)。
 *
 *   5. **検証と派生の分離**: `isValidRecoveryMnemonic()` (純判定、副作用ゼロ) と
 *      `deriveRecoverySeed()` (PBKDF2 経由で seed 生成) を独立 export。
 *      UI 側は入力検証だけしたい場面 (リアルタイム入力ハイライト等) で前者だけ使える。
 *
 * 既知の TODO (T-C5b 着手時に決定):
 *
 *   - `RECOVERY_KEY` (= seed の HKDF 派生) で何を unwrap するか (= 第 2 の sealed SK か、
 *     直接 SK か) — KEY_HIERARCHY.md に Recovery Key 経路を §6 として追記する別 PR で確定
 *   - サーバ API endpoint (`POST /auth/recover` 等) — 現状 backend は未実装
 */

const MNEMONIC_WORD_COUNT = 24

/**
 * 入力された mnemonic 文字列が valid な BIP39 24-word phrase かを判定する。
 *
 * - 空白で split したとき 24 単語であること
 * - すべて wordlist (English) に含まれること
 * - 最後の単語に埋め込まれた checksum が一致すること
 *
 * 副作用なし。UI 側のリアルタイム検証 (入力中の「あと N 単語」表示等) で使える。
 */
export const isValidRecoveryMnemonic = (mnemonic: string): boolean => {
  // `validateMnemonic` は内部で空白 split / wordlist 検証 / checksum 検証を行う
  return validateMnemonic(mnemonic, ENGLISH_WORDLIST)
}

/**
 * mnemonic → 64 byte seed (PBKDF2-HMAC-SHA512, 2048 反復、salt = "mnemonic" + passphrase)。
 *
 * BIP39 標準では passphrase は user-supplied だが、OoKey では空文字 (passphrase なし) で
 * 固定する (UI の複雑度を抑えるため)。
 *
 * 戻り値の Uint8Array は **呼出側で zeroize 責任**。HKDF で派生させた直後に
 * `zeroize(seed)` で破棄すること (CLAUDE.md §2 ルール 1)。
 *
 * @throws BIP39 として無効な mnemonic を渡すと `@scure/bip39` が throw する。
 *   呼出前に `isValidRecoveryMnemonic()` で検証することを推奨。
 */
export const deriveRecoverySeed = async (mnemonic: string): Promise<Uint8Array> => {
  const seed = await mnemonicToSeed(mnemonic, '')

  return seed
}

/** 期待単語数 (24)。UI 側の hint 表示用に export。 */
export const RECOVERY_MNEMONIC_WORD_COUNT = MNEMONIC_WORD_COUNT

/**
 * 256-bit entropy で BIP39 24-word mnemonic を新規生成する (T-C5b 登録時)。
 *
 * - `crypto.getRandomValues` を内部で使用 (`@scure/bip39` の strength=256 経路)
 * - English wordlist 固定 (`isValidRecoveryMnemonic` と一致)
 * - 戻り値は **ユーザーに一回限り表示し、紙保管を促す** 性質のもの。
 *   `RegisterPage` 側で表示 → 確認チェック後 zeroize 不要 (string は GC のみ、
 *   Uint8Array ではないため `fill(0)` できない。React state からの参照を切ることで対処)
 *
 * セキュリティ:
 *   - 平文として一時的にメモリに存在するが、ユーザー入力を必須として
 *     紙保管確認後に state を空文字に上書きする (UI 責務)
 *   - サーバには絶対に送信しない (KEY_HIERARCHY.md §2.2)
 */
export const generateRecoveryMnemonic = (): string => {
  // strength=256 → entropy 32 byte → 24 単語
  return generateMnemonic(ENGLISH_WORDLIST, 256)
}
